import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { claimNewProfileRegistration } from '@/features/profile/profile-onboarding'

import { fetchCurrentUser, login, loginWithGoogle, logout } from './api'
import type { LoginInput, Session } from './schema'
import { isAuthenticated, useAuthStore, useUser } from './store'

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
}

export function currentUserQueryOptions() {
  return queryOptions({
    queryKey: authKeys.me(),
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    enabled: isAuthenticated(),
    staleTime: 5 * 60_000,
  })
}

/** Live profile for header chips. Persist is the fallback until `/me` returns. */
export function useCurrentUser() {
  const stored = useUser()
  const setUser = useAuthStore((s) => s.setUser)
  const query = useQuery(currentUserQueryOptions())

  useEffect(() => {
    if (query.data) setUser(query.data)
  }, [query.data, setUser])

  return query.data ?? stored
}

function usePersistSession() {
  const setSession = useAuthStore((s) => s.setSession)
  const queryClient = useQueryClient()

  return (session: Session) => {
    // A previous session may still hold group/order data (the route guard only
    // clears the token). Drop it so the new account never reads another Jastiper's
    // cache — that is what let the dashboard load with zero groups.
    queryClient.clear()
    claimNewProfileRegistration(session.user.id, session.user.phone)
    setSession(session)
    // Login already returns the profile — seed cache instead of invalidating (avoids duplicate /me).
    queryClient.setQueryData(authKeys.me(), session.user)
  }
}

export function useLogin() {
  const persistSession = usePersistSession()

  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: persistSession,
  })
}

export function useGoogleLogin() {
  const persistSession = usePersistSession()

  return useMutation({
    mutationFn: (idToken: string) => loginWithGoogle(idToken),
    onSuccess: persistSession,
  })
}

export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: logout,
    // Drop the local session either way — a failed logout must not strand the user.
    onSettled: () => {
      clearSession()
      queryClient.clear()
      void navigate({ to: '/login', replace: true })
    },
  })
}
